import React, { useState, useEffect, useRef } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import {
  useAuth0,
  withAuth0,
  withAuthenticationRequired,
} from '@auth0/auth0-react';
import axios from 'axios';
import ProviderChat from './chat/ProviderChat';
import { Footer, LoadingPage, NoAccessPage } from 'cj-common-react';
import NavBar from './NavBar';
import { Box, useTheme } from '@chakra-ui/react';
import AdminConfig from './admin/AdminConfig';
import {
  config,
  READ_PROVIDER_MESSAGE_PERMISSION,
  USER_PERMISSIONS_NAMESPACE_KEY,
} from '../config';

function ProtectedRoutes({ featureFlags, userLocation }) {
  const theme = useTheme();
  const [activeChatConversationId, setActiveChatConversationId] = useState('');
  const [
    activeProviderChatConversationId,
    setActiveProviderChatConversationId,
  ] = useState('');
  const [expandedView, setExpandedView] = useState(false);
  const [devMode, setDevMode] = useState(false);
  const [chatConversations, setChatConversations] = useState([]);
  const [providerChatConversations, setProviderChatConversations] = useState(
    [],
  );
  const [latestChatServerMessage, setLatestChatServerMessage] = useState({});
  const [latestProviderChatServerMessage, setLatestProviderChatServerMessage] =
    useState({});
  const [chatMessages, setChatMessages] = useState([]);
  const [providerChatMessages, setProviderChatMessages] = useState([]);
  const [providerChatLatestSearchResults, setProviderChatLatestSearchResults] =
    useState(null);
  const [providerChatSearchParameters, setProviderChatSearchParameters] =
    useState(null);
  const [
    providerChatTotalSearchResultsCount,
    setProviderChatTotalSearchResultsCount,
  ] = useState(null);
  const [isChatSidebarOpen, setIsChatSidebarOpen] = useState(true);
  const [isProviderChatSidebarOpen, setIsProviderChatSidebarOpen] =
    useState(false);

  const { getAccessTokenSilently } = useAuth0();
  const chatSocketRef = useRef();
  const providerChatSocketRef = useRef();
  const { user } = useAuth0();

  const handleNewChatServerMessage = (event) => {
    const message = JSON.parse(event.data);
    const newMessage = {
      ...message, // text, data, dataType, postText
      sender: 'ChatGPT',
    };
    setLatestChatServerMessage((prevMessage) => newMessage);
  };

  const handleNewProviderChatServerMessage = (event) => {
    const message = JSON.parse(event.data);
    const newMessage = {
      ...message, // text, data, dataType, postText
      sender: 'ChatGPT',
    };
    setLatestProviderChatServerMessage((prevMessage) => newMessage);
  };

  const sendMessageChat = (message) => {
    chatSocketRef.current.send(
      JSON.stringify({
        action: 'chatFlow',
        data: {
          message: message.text,
          conversationId: activeChatConversationId,
        },
      }),
    );
  };

  const sendUserChatMessageToServer = (message) => {
    if (chatSocketRef.current.readyState !== WebSocket.OPEN) {
      console.debug(
        'WebSocket is NOT OPEN, attempting to reconnect VBC chat websocket',
      );
      const onOpenAndSendMessage = () => {
        sendMessageChat(message);
        chatSocketRef.current.removeEventListener('open', onOpenAndSendMessage);
      };

      chatSocketRef.current.addEventListener('open', onOpenAndSendMessage, {
        once: true,
      });
      fetchAccessTokenAndInitializeWebSocket(
        chatSocketRef,
        handleNewProviderChatServerMessage,
        () => {
          console.debug('WebSocket has been re-opened, sending message');
          sendMessageChat(message);
        },
        () => {
          console.debug('VBC Chat Websocket Connection closed');
        },
      );
      return;
    }
    sendMessageChat(message);
  };

  const sendMessageProvider = (message, conversationId) => {
    providerChatSocketRef.current.send(
      JSON.stringify({
        action: 'providerChatFlow',
        data: {
          message: message.text,
          conversationId,
          userLocation, // attach user location to all providerChatFlow messages
        },
      }),
    );
  };

  const sendUserProviderChatMessageToServer = (message, conversationId) => {
    if (!conversationId && !activeProviderChatConversationId) {
      console.debug(
        'tried sending message to server but conversationId = ' +
          activeProviderChatConversationId,
      );
      return;
    }
    if (providerChatSocketRef.current.readyState !== WebSocket.OPEN) {
      console.debug(
        'WebSocket is NOT OPEN, attempting to reconnect provider chat websocket',
      );
      const onOpenAndSendMessage = () => {
        sendMessageProvider(message, conversationId);
        providerChatSocketRef.current.removeEventListener(
          'open',
          onOpenAndSendMessage,
        );
      };

      providerChatSocketRef.current.addEventListener(
        'open',
        onOpenAndSendMessage,
        { once: true },
      );
      fetchAccessTokenAndInitializeWebSocket(
        providerChatSocketRef,
        handleNewProviderChatServerMessage,
        () => {
          console.debug('WebSocket has been re-opened, sending message');
          sendMessageProvider(message, conversationId);
        },
        () => {
          console.debug('Provider Chat Websocket Connection closed');
        },
      );
      return;
    }
    sendMessageProvider(message, conversationId);
  };

  const getConversations = async (conversationType) => {
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.get(
        `${config.HTTP_API_URL}/conversations/conversationType/${conversationType}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      console.debug('conversations fetched: ', response.data);
      return response.data.conversations;
    } catch (error) {
      console.debug(
        `Couldn't get conversations for conversationType ${conversationType}`,
      );
      console.debug(error);
    }
  };

  const getMessagesForConversation = async (conversationId) => {
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.get(
        `${config.HTTP_API_URL}/conversations/${conversationId}/messages`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.debug(
        `Couldn't get messages for conversationId ${conversationId}`,
      );
      console.debug(error);
    }
  };

  const getLatestSearchResultsForConversation = async (conversationId) => {
    try {
      const accessToken = await getAccessTokenSilently();
      let url = `${config.HTTP_API_URL}/conversations/${conversationId}/searchResults/latest`;
      if (userLocation.latitude && userLocation.longitude) {
        const queryParams = new URLSearchParams();
        for (const [key, value] of Object.entries(userLocation)) {
          queryParams.append(key, value);
        }
        url += `?${queryParams.toString()}`;
      }
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response.data;
    } catch (error) {
      console.debug(
        `Couldn't get latest search results for conversationId ${conversationId}`,
      );
      console.debug(error);
    }
  };

  const fetchAccessTokenAndInitializeWebSocket = async (
    socketRef,
    serverMessageHandler,
    onOpen,
    onClose,
  ) => {
    const accessToken = await getAccessTokenSilently();
    const socket = new WebSocket(
      `${config.WS_API_URL}?Auth=${encodeURIComponent(
        'Bearer ' + accessToken,
      )}`,
    );
    socket.addEventListener('message', serverMessageHandler);
    socket.onopen = onOpen;
    socket.onclose = onClose;
    socketRef.current = socket; // Store the WebSocket connection in the ref
  };

  useEffect(async () => {
    // get existing conversations and initialize websocket connections
    const [chatConversations, providerChatConversations] = await Promise.all([
      getConversations('VBC'),
      getConversations('PROVIDER'),
      fetchAccessTokenAndInitializeWebSocket(
        chatSocketRef,
        handleNewChatServerMessage,
        () => {
          console.debug('VBC Chat Websocket Connection open');
        },
        () => {
          console.debug('VBC Chat Websocket Connection closed');
        },
      ),
      fetchAccessTokenAndInitializeWebSocket(
        providerChatSocketRef,
        handleNewProviderChatServerMessage,
        () => {
          console.debug('Provider Chat Websocket Connection open');
        },
        () => {
          console.debug('Provider Chat Websocket Connection closed');
        },
      ),
    ]);
    setChatConversations(chatConversations);
    setProviderChatConversations(providerChatConversations);

    // Cleanup logic to close WebSocket connection when the component unmounts
    return () => {
      if (chatSocketRef.current) {
        chatSocketRef.current.close();
      }
      if (providerChatSocketRef.current) {
        providerChatSocketRef.current.close();
      }
    };
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!activeChatConversationId) {
      return;
    }
    const fetchMessages = async () => {
      const messages = await getMessagesForConversation(
        activeChatConversationId,
      );
      setChatMessages(messages);
    };
    fetchMessages();
  }, [activeChatConversationId]);

  useEffect(() => {
    if (!activeProviderChatConversationId) {
      console.debug(
        'no activeProviderChatConversationId, breaking out of useEffect before fetching new messages',
      );
      return;
    }
    const isNewConversation = (conversationId) =>
      providerChatConversations.find(
        (conversation) => conversation.conversationId === conversationId,
      )?.isNewConversation === true;
    if (isNewConversation(activeProviderChatConversationId)) {
      console.debug(
        'new conversation, breaking out of useEffect before fetching new messages',
      );
      return;
    }
    const fetchMessages = async () => {
      const messages = await getMessagesForConversation(
        activeProviderChatConversationId,
      );
      setProviderChatMessages(messages);
    };
    const fetchLatestSearchResults = async () => {
      const latestSearchResults = await getLatestSearchResultsForConversation(
        activeProviderChatConversationId,
      );
      if (latestSearchResults) {
        const { searchResults, searchParams, totalResultsCount } =
          latestSearchResults;
        setProviderChatSearchParameters(searchParams);
        setProviderChatLatestSearchResults(searchResults);
        setProviderChatTotalSearchResultsCount(totalResultsCount);
      } else {
        setProviderChatSearchParameters(null);
        setProviderChatLatestSearchResults(null);
        setProviderChatTotalSearchResultsCount(null);
      }
    };
    fetchMessages();
    fetchLatestSearchResults();
  }, [activeProviderChatConversationId]);

  return (
    <Box bg={theme.colors.bgGrey2} alignItems='center'>
      <NavBar expandedView={expandedView} ldFeatureFlags={featureFlags} />
      <Switch>
        <Route
          exact
          path='/'
          render={() => (
            <Redirect to='/providerBot' />
            // <Chat
            //   activeConversationId={activeChatConversationId}
            //   setActiveConversationId={setActiveChatConversationId}
            //   conversations={chatConversations}
            //   setConversations={setChatConversations}
            //   messages={chatMessages}
            //   setMessages={setChatMessages}
            //   latestServerMessage={latestChatServerMessage}
            //   sendUserMessageToServer={sendUserChatMessageToServer}
            //   isSidebarOpen={isChatSidebarOpen}
            //   setIsSidebarOpen={setIsChatSidebarOpen}
            //   expandedView={expandedView}
            //   toggleExpandedView={() => setExpandedView(!expandedView)}
            //   devMode={devMode}
            //   toggleDevMode={() => setDevMode(!devMode)}
            // />
          )}
        />
        <Route
          exact
          path='/providerBot'
          render={(props) => {
            if (
              !user[USER_PERMISSIONS_NAMESPACE_KEY].includes(
                READ_PROVIDER_MESSAGE_PERMISSION,
              ) &&
              featureFlags?.chatAppUseRbac
            ) {
              return (
                <NoAccessPage
                  handleClick={() =>
                    window.location.replace(config.CJ_HOME_BASE_URL)
                  }
                />
              );
            }

            return (
              <ProviderChat
                activeConversationId={activeProviderChatConversationId}
                setActiveConversationId={setActiveProviderChatConversationId}
                conversations={providerChatConversations}
                setConversations={setProviderChatConversations}
                messages={providerChatMessages}
                setMessages={setProviderChatMessages}
                latestSearchResults={providerChatLatestSearchResults}
                setLatestSearchResults={setProviderChatLatestSearchResults}
                searchParameters={providerChatSearchParameters}
                setSearchParameters={setProviderChatSearchParameters}
                totalSearchResultsCount={providerChatTotalSearchResultsCount}
                setTotalSearchResultsCount={
                  setProviderChatTotalSearchResultsCount
                }
                latestServerMessage={latestProviderChatServerMessage}
                sendUserMessageToServer={sendUserProviderChatMessageToServer}
                isSidebarOpen={isProviderChatSidebarOpen}
                setIsSidebarOpen={setIsProviderChatSidebarOpen}
                expandedView={expandedView}
                toggleExpandedView={() => setExpandedView(!expandedView)}
                devMode={devMode}
                toggleDevMode={() => setDevMode(!devMode)}
                {...props}
              />
            );
          }}
        />
        <Route path='/admin' render={() => <AdminConfig />} />
      </Switch>
      {!expandedView && <Footer />}
    </Box>
  );
}

export default withAuthenticationRequired(withAuth0(ProtectedRoutes), {
  onRedirecting: () => <LoadingPage />,
});
